import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import { Row, Col, Card, Table, Badge, Form, Button, Spinner, Alert } from 'react-bootstrap';
import client from '../../modules/client';
import { Colors } from '../../modules/theme';

const StatCard = ({ label, value, variant }) => (
  <Card
    className="text-center mb-3"
    style={{ borderLeft: `4px solid ${variant}`, borderRadius: 8 }}
  >
    <Card.Body style={{ padding: '14px 20px' }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: variant }}>{value}</div>
      <div style={{ fontSize: 13, color: Colors.gray }}>{label}</div>
    </Card.Body>
  </Card>
);

StatCard.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  variant: PropTypes.string,
};

const statusBadge = confirmedUsed => {
  if (confirmedUsed === true) return <Badge bg="success">Used</Badge>;
  if (confirmedUsed === false) return <Badge bg="secondary">Skipped</Badge>;
  return (
    <Badge bg="warning" text="dark">
      Pending
    </Badge>
  );
};

export default class RewardStatsPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      stats: null,
      loading: false,
      error: null,
      from: '',
      to: '',
      cardName: '',
    };
  }

  componentDidMount() {
    this.load();
  }

  load = async () => {
    const { from, to, cardName } = this.state;
    this.setState({ loading: true, error: null });
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    if (cardName.trim()) params.cardName = cardName.trim();

    const res = await client.getRewardStats(params);
    if (res.ok) {
      this.setState({ stats: res.data, loading: false });
    } else {
      this.setState({ error: res.data?.error?.message || 'Failed to load stats', loading: false });
    }
  };

  render() {
    const { stats, loading, error, from, to, cardName } = this.state;

    return (
      <div className="pt-4">
        {/* Filters */}
        <Row className="mb-4 align-items-end">
          <Col xs={12} sm={4} md={3}>
            <Form.Label style={{ fontSize: 12 }}>From date</Form.Label>
            <Form.Control
              type="date"
              value={from}
              onChange={e => this.setState({ from: e.target.value })}
            />
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Form.Label style={{ fontSize: 12 }}>To date</Form.Label>
            <Form.Control
              type="date"
              value={to}
              onChange={e => this.setState({ to: e.target.value })}
            />
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Form.Label style={{ fontSize: 12 }}>Card name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. Chase Sapphire"
              value={cardName}
              onChange={e => this.setState({ cardName: e.target.value })}
            />
          </Col>
          <Col xs={12} sm={4} md={3} className="mt-2 mt-sm-0">
            <Button variant="primary" onClick={this.load} disabled={loading} className="w-100">
              {loading ? <Spinner size="sm" animation="border" /> : 'Apply'}
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}

        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {!loading && stats && (
          <>
            {/* Stat cards */}
            <Row className="mb-4">
              <Col xs={6} sm={3}>
                <StatCard label="Total Sent" value={stats.totalSent} variant={Colors.blue2} />
              </Col>
              <Col xs={6} sm={3}>
                <StatCard label="Confirmed Used" value={stats.confirmedYes} variant="#28a745" />
              </Col>
              <Col xs={6} sm={3}>
                <StatCard label="Skipped" value={stats.confirmedNo} variant={Colors.gray} />
              </Col>
              <Col xs={6} sm={3}>
                <StatCard label="Pending" value={stats.pending} variant="#ffc107" />
              </Col>
            </Row>

            <div className="mb-4 text-center">
              <span style={{ fontSize: 15, color: Colors.gray }}>Confirmation rate: </span>
              <strong style={{ fontSize: 20 }}>{stats.confirmationRate}%</strong>
              <span style={{ fontSize: 13, color: Colors.gray, marginLeft: 6 }}>
                (users who confirmed they used the recommended card)
              </span>
            </div>

            {/* Recent alerts table */}
            <h5 className="mb-3" style={{ fontWeight: 600 }}>
              Recent Reward Alerts
            </h5>
            {stats.recentAlerts.length === 0 ? (
              <Alert variant="info">No reward alerts found for this filter.</Alert>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <Table bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Card</th>
                      <th>Bank</th>
                      <th>Store</th>
                      <th>Reward</th>
                      <th>Rate</th>
                      <th>Status</th>
                      <th>Confirmed At</th>
                      <th>Sent At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentAlerts.map(alert => {
                      const rateLabel =
                        alert.rewardRate != null && alert.rewardType
                          ? alert.rewardType === 'cashback'
                            ? `${alert.rewardRate}% cashback`
                            : `${alert.rewardRate}x ${alert.rewardType}`
                          : alert.reward || '—';

                      return (
                        <tr key={alert.id}>
                          <td>{alert.cardName || '—'}</td>
                          <td>{alert.bankName || '—'}</td>
                          <td>{alert.placeName || '—'}</td>
                          <td>{alert.reward || '—'}</td>
                          <td>{rateLabel}</td>
                          <td>{statusBadge(alert.confirmedUsed)}</td>
                          <td>
                            {alert.confirmedAt
                              ? Moment(alert.confirmedAt).format('MM/DD/YY HH:mm')
                              : '—'}
                          </td>
                          <td>
                            {alert.createdAt
                              ? Moment(alert.createdAt).format('MM/DD/YY HH:mm')
                              : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}
